import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Avatar,
  Stack,
  Grid,
  Dialog,
  DialogContent,
  ImageList,
  ImageListItem,
  CardMedia,
  TextField,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  ThumbUp as ThumbUpIcon,
  Schedule as ScheduleIcon,
  Star as StarIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  Image as ImageIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  Comment as CommentIcon,
  Reply as ReplyIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Send as SendIcon,
  ThumbUpOutlined as ThumbUpOutlinedIcon
} from '@mui/icons-material';
import { FaultTopic, TopicComment } from '../../types/maintenanceBank';
import { FaultTopicService } from '../../services/maintenanceBankService';
import { useAuth } from '../../contexts/AuthContext';
import TopicComments from '../../components/TopicComments';

const TopicViewPage: React.FC = () => {
  const { topicId } = useParams<{ topicId: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [topic, setTopic] = useState<FaultTopic | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // حالة معرض الصور
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // حالة التعليقات (للاستخدام المستقبلي)
  // const [comments, setComments] = useState<TopicComment[]>([]);
  // const [newComment, setNewComment] = useState('');
  // const [replyTo, setReplyTo] = useState<string | null>(null);
  // const [replyContent, setReplyContent] = useState('');
  // const [editingComment, setEditingComment] = useState<string | null>(null);
  // const [editContent, setEditContent] = useState('');
  // const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  // const [selectedComment, setSelectedComment] = useState<string | null>(null);

  // تحميل الموضوع
  const loadTopic = async () => {
    if (!topicId) {
      setError('معرف الموضوع مفقود');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      console.log('🔄 تحميل الموضوع:', topicId);
      
      const topicData = await FaultTopicService.getTopic(topicId);
      
      if (!topicData) {
        setError('الموضوع غير موجود');
        return;
      }
      
      setTopic(topicData);
      console.log('✅ تم تحميل الموضوع بنجاح:', topicData);
    } catch (error: any) {
      console.error('❌ خطأ في تحميل الموضوع:', error);
      setError(`فشل في تحميل الموضوع: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTopic();
  }, [topicId]);

  // العودة للخلف
  const handleBack = () => {
    navigate('/admin/maintenance-bank');
  };

  // تعديل الموضوع
  const handleEdit = () => {
    navigate(`/admin/maintenance-bank/topic/${topicId}/edit`);
  };

  // فتح معرض الصور
  const handleOpenImageDialog = (index: number) => {
    setSelectedImageIndex(index);
    setImageDialogOpen(true);
  };

  // الانتقال للصورة التالية
  const handleNextImage = () => {
    if (topic && topic.images) {
      setSelectedImageIndex((prev) => (prev + 1) % topic.images.length);
    }
  };

  // الانتقال للصورة السابقة
  const handlePrevImage = () => {
    if (topic && topic.images) {
      setSelectedImageIndex((prev) => (prev - 1 + topic.images.length) % topic.images.length);
    }
  };

  // تحديد لون مستوى الصعوبة
  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'success';
      case 'medium': return 'warning';
      case 'hard': return 'error';
      default: return 'default';
    }
  };

  // تحديد نص مستوى الصعوبة
  const getDifficultyText = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'سهل';
      case 'medium': return 'متوسط';
      case 'hard': return 'صعب';
      default: return difficulty;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          العودة
        </Button>
      </Box>
    );
  }

  if (!topic) {
    return (
      <Box p={3}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          الموضوع غير موجود
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          العودة
        </Button>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* شريط التنقل العلوي */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={handleBack}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" component="h1">
            ��رض الموضوع
          </Typography>
        </Box>
        
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleEdit}
          >
            تعديل
          </Button>
        </Box>
      </Box>

      {/* محتوى الموضوع */}
      <Paper elevation={2} sx={{ p: 3 }}>
        {/* العنوان والحالة */}
        <Box mb={3}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="h4" component="h2" gutterBottom>
              {topic.title}
            </Typography>
            
            <Box display="flex" gap={1}>
              {!topic.isPublished && (
                <Chip
                  label="مسودة"
                  color="warning"
                  variant="outlined"
                  size="small"
                />
              )}
              <Chip
                label={getDifficultyText(topic.difficulty)}
                color={getDifficultyColor(topic.difficulty) as any}
                size="small"
              />
            </Box>
          </Box>

          {/* الوصف */}
          {topic.description && (
            <Typography variant="body1" color="text.secondary" paragraph>
              {topic.description}
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* معلومات إضافية */}
        <Box mb={3}>
          <Stack direction="row" spacing={3} flexWrap="wrap">
            <Box display="flex" alignItems="center" gap={1}>
              <ScheduleIcon color="action" fontSize="small" />
              <Typography variant="body2" color="text.secondary">
                {topic.estimatedTime} دقيقة
              </Typography>
            </Box>
            
            <Box display="flex" alignItems="center" gap={1}>
              <ViewIcon color="action" fontSize="small" />
              <Typography variant="body2" color="text.secondary">
                {topic.views || 0} مشاهدة
              </Typography>
            </Box>
            
            <Box display="flex" alignItems="center" gap={1}>
              <ThumbUpIcon color="action" fontSize="small" />
              <Typography variant="body2" color="text.secondary">
                {topic.likes || 0} إعجاب
              </Typography>
            </Box>
            
            <Box display="flex" alignItems="center" gap={1}>
              <CalendarIcon color="action" fontSize="small" />
              <Typography variant="body2" color="text.secondary">
                {topic.createdAt?.toLocaleDateString('ar-EG')}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* معرض الصور */}
        {topic.images && topic.images.length > 0 && (
          <Box mb={3}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ImageIcon />
              معرض الصور ({topic.images.length})
            </Typography>
            
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {topic.images.map((image, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card 
                    sx={{ 
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: 4
                      }
                    }}
                    onClick={() => handleOpenImageDialog(index)}
                  >
                    <Box sx={{ position: 'relative' }}>
                      <CardMedia
                        component="img"
                        height="200"
                        image={image}
                        alt={`صورة ${index + 1}`}
                        sx={{ objectFit: 'cover' }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          bgcolor: 'rgba(0, 0, 0, 0)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            bgcolor: 'rgba(0, 0, 0, 0.5)'
                          }
                        }}
                      >
                        <ZoomInIcon 
                          sx={{ 
                            color: 'white',
                            fontSize: 40,
                            opacity: 0,
                            transition: 'opacity 0.3s ease',
                            '.MuiCard-root:hover &': {
                              opacity: 1
                            }
                          }} 
                        />
                      </Box>
                      <Chip
                        label={`${index + 1}/${topic.images.length}`}
                        size="small"
                        sx={{
                          position: 'absolute',
                          bottom: 8,
                          right: 8,
                          bgcolor: 'rgba(0, 0, 0, 0.7)',
                          color: 'white'
                        }}
                      />
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {topic.images && topic.images.length > 0 && <Divider sx={{ my: 3 }} />}

        {/* المحتوى */}
        <Box>
          <Typography variant="h6" gutterBottom>
            المحتوى:
          </Typography>
          
          <Card variant="outlined">
            <CardContent>
              <Box
                dangerouslySetInnerHTML={{ __html: topic.content }}
                sx={{
                  '& img': {
                    maxWidth: '100%',
                    height: 'auto',
                    borderRadius: 1,
                    cursor: 'pointer',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.02)'
                    }
                  },
                  '& blockquote': {
                    borderLeft: '4px solid #2196f3',
                    pl: 2,
                    fontStyle: 'italic',
                    bgcolor: 'grey.50',
                    py: 1,
                    my: 2
                  },
                  '& pre': {
                    bgcolor: 'grey.100',
                    p: 2,
                    borderRadius: 1,
                    overflow: 'auto'
                  },
                  '& code': {
                    bgcolor: 'grey.100',
                    px: 1,
                    py: 0.5,
                    borderRadius: 0.5,
                    fontFamily: 'monospace'
                  },
                  '& ul, & ol': {
                    pl: 3
                  },
                  '& table': {
                    width: '100%',
                    borderCollapse: 'collapse',
                    my: 2
                  },
                  '& th, & td': {
                    border: '1px solid',
                    borderColor: 'divider',
                    p: 1
                  },
                  '& th': {
                    bgcolor: 'grey.100',
                    fontWeight: 600
                  }
                }}
              />
            </CardContent>
          </Card>
        </Box>

        {/* العلامات */}
        {topic.tags && topic.tags.length > 0 && (
          <Box mt={3}>
            <Typography variant="h6" gutterBottom>
              العلامات:
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              {topic.tags.map((tag: string, index: number) => (
                <Chip
                  key={index}
                  label={tag}
                  variant="outlined"
                  size="medium"
                  sx={{
                    '&:hover': {
                      bgcolor: 'primary.50'
                    }
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
      </Paper>

      {/* قسم التعليقات */}
      {topicId && <TopicComments topicId={topicId} />}

      {/* حوار عرض الصورة بالحجم الكامل */}
      <Dialog
        open={imageDialogOpen}
        onClose={() => setImageDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'rgba(0, 0, 0, 0.9)',
            boxShadow: 'none'
          }
        }}
      >
        <DialogContent sx={{ p: 0, position: 'relative', bgcolor: 'black' }}>
          {/* زر الإغلاق */}
          <IconButton
            onClick={() => setImageDialogOpen(false)}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: 'white',
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1,
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.7)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* أزرار التنقل */}
          {topic.images && topic.images.length > 1 && (
            <>
              <IconButton
                onClick={handlePrevImage}
                sx={{
                  position: 'absolute',
                  left: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'white',
                  bgcolor: 'rgba(0, 0, 0, 0.5)',
                  zIndex: 1,
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.7)'
                  }
                }}
              >
                <NavigateBeforeIcon fontSize="large" />
              </IconButton>

              <IconButton
                onClick={handleNextImage}
                sx={{
                  position: 'absolute',
                  right: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'white',
                  bgcolor: 'rgba(0, 0, 0, 0.5)',
                  zIndex: 1,
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.7)'
                  }
                }}
              >
                <NavigateNextIcon fontSize="large" />
              </IconButton>
            </>
          )}

          {/* الصورة */}
          {topic.images && topic.images[selectedImageIndex] && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '70vh',
                p: 2
              }}
            >
              <img
                src={topic.images[selectedImageIndex]}
                alt={`صورة ${selectedImageIndex + 1}`}
                style={{
                  maxWidth: '100%',
                  maxHeight: '80vh',
                  objectFit: 'contain'
                }}
              />
            </Box>
          )}

          {/* عداد الصور */}
          {topic.images && topic.images.length > 1 && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                bgcolor: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                px: 2,
                py: 1,
                borderRadius: 2
              }}
            >
              <Typography variant="body2">
                {selectedImageIndex + 1} / {topic.images.length}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default TopicViewPage;
